import { z } from 'zod'

// A Booking check asks staff to look for a Flio booking that NexHealth has
// not yet linked to a record in the clinic's practice system
// (services/dashboard-booking-check-core). Staff record what they found or
// did; that never confirms the booking. Only NexHealth's readback does, and
// it closes the check itself.

export const bookingCheckKindSchema = z.enum([
  'NOT_IN_PMS',
  'POSSIBLE_DUPLICATE',
])
export const bookingCheckReasonSchema = z.enum([
  'ONLINE_NOT_WRITTEN',
  'APPOINTMENT_SOON',
  'APPOINTMENT_NOT_FOUND',
  'STATUS_UNAVAILABLE',
  'REACHED_PMS_AFTER_STAFF_RESOLUTION',
])
export const staffResolutionSchema = z.enum([
  'FOUND_IN_PMS',
  'ADDED_TO_PMS',
  'HANDLED_WITH_PATIENT',
  'DUPLICATE_HANDLED',
])
const resolutionTypeSchema = z.enum([
  'FOUND_IN_PMS',
  'ADDED_TO_PMS',
  'HANDLED_WITH_PATIENT',
  'DUPLICATE_HANDLED',
  'REACHED_PMS',
  'BOOKING_CANCELLED',
])

const timestamp = z.number().int().positive()

const appointmentSchema = z
  .object({
    patientDisplayName: z.string().max(1000).nullable(),
    scheduledAtMs: timestamp.nullable(),
    scheduledAtIso: z.string().max(64).nullable(),
    clinicTimeZone: z.string().max(64).nullable(),
    providerName: z.string().max(1000).nullable(),
    operatoryName: z.string().max(1000).nullable(),
    appointmentTypeName: z.string().max(1000).nullable(),
    pmsState: z.enum(['confirmed', 'pending']).nullable(),
    status: z.string().max(32).nullable(),
  })
  .strict()

export const bookingCheckSchema = z
  .object({
    bookingCheckId: z.string().regex(/^dbc_v1_[a-f0-9]{64}$/),
    kind: bookingCheckKindSchema,
    reason: bookingCheckReasonSchema,
    appointmentId: z.string().regex(/^nhrlink_v1_[a-f0-9]{64}$/),
    sourceChannel: z.enum(['VOICE', 'SMS', 'DASHBOARD']),
    newPatient: z.boolean(),
    status: z.enum(['OPEN', 'RESOLVED']),
    createdAtMs: timestamp,
    updatedAtMs: timestamp,
    revision: z.number().int().min(1).max(2),
    resolution: z
      .object({
        resolutionType: resolutionTypeSchema,
        resolvedBy: z.enum(['STAFF', 'SYSTEM']),
        resolvedAtMs: timestamp,
      })
      .strict()
      .nullable(),
    appointment: appointmentSchema.nullable(),
  })
  .strict()

export const bookingChecksResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    watchActive: z.boolean(),
    openCount: z.number().int().min(0),
    items: z.array(bookingCheckSchema).max(50),
  })
  .strict()

export const bookingCheckResolutionResponseSchema = bookingCheckSchema
  .extend({ outcome: z.enum(['RESOLVED', 'EXACT_REPLAY']) })
  .strict()

export type BookingCheck = z.infer<typeof bookingCheckSchema>
export type BookingChecksResponse = z.infer<typeof bookingChecksResponseSchema>
export type StaffResolution = z.infer<typeof staffResolutionSchema>
